import { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

import { UIImageUploadDialog } from '@/components/dialogs/ui-image-upload-dialog';
import { useAccountChanges } from '@/modules/mcpStream';
import LureoButton from '@/common/LureoButton';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Avatar } from '@/common/Avatar';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { useConsent } from '@/modules/cookieConsent';
import { PlanSelectionModal } from '@/components/dialogs/plan-selection-modal';
import { VoucherRedeemModal } from '@/components/dialogs/voucher-redeem-modal';
import { PageTitle } from '@/components/ui/text';
import { EmptyState } from '@/components/ui/empty-state';
import { PageFrame } from '@/components/ui/page-frame';
import { useAccountMembership } from './account/useAccountMembership';
import { useProfileForm } from './account/useProfileForm';
import { useAvatarSave } from './account/useAvatarSave';
import { AccountSettingsTab } from './account/AccountSettingsTab';
import { AccountPage } from '@/components/account/account-page';
import { ProfileCard } from '@/components/account/profile-card';
import { SecurityCard } from '@/components/account/security-card';
import { SubscriptionCard } from '@/components/account/subscription-card';
import { PasswordLinkPrompt } from '@/components/account/password-link-prompt';

export default function MyAccount() {
  const navigate = useNavigate();
  const { user, signOut, refreshUser } = useAuth();
  const { openPreferences: openCookiePreferences } = useConsent();

  const membership = useAccountMembership(user?.id);
  const { orgMember, orgInstance, status: membershipStatus } = membership;
  const membershipKnown = membershipStatus === 'ready' && orgMember !== null;
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [avatarRefreshTrigger, setAvatarRefreshTrigger] = useState(0);
  const avatar = useAvatarSave(user?.id, avatarRefreshTrigger);
  const {
    formData,
    setFormData,
    saving,
    isOwner,
    emailChanged,
    canSaveProfile,
    updateProfile,
    updatePassword,
    sendPasswordLink,
    resetSent,
  } = useProfileForm({
    user,
    orgMember,
    orgInstance,
    membershipKnown,
    refreshUser,
    updateMember: membership.updateMember,
    updateInstance: membership.updateInstance,
  });

  // Enable scrolling for MyAccount page
  useLayoutEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    // Add scrollbar-gutter to prevent content shift
    document.body.style.scrollbarGutter = 'stable';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.scrollbarGutter = '';
    };
  }, []);

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  // Changed elsewhere (Claude through MCP, another tab): read again in place, fields being edited keep their edits
  useAccountChanges(['orgMember', 'org'], () => membership.refresh(), 500);
  useAccountChanges(['image'], () => setAvatarRefreshTrigger(n => n + 1), 500);

  const handleProfileKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void updateProfile();
    }
  };

  // Enter in the new email field moves to the password it needs, or saves when it needs none
  const handleNewEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (emailChanged) document.getElementById('profile_current_password')?.focus();
      else void updateProfile();
    }
  };

  const focusOnEnter = (nextId: string) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      document.getElementById(nextId)?.focus();
    }
  };

  const handleConfirmPasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void updatePassword();
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const handleVoucherSuccess = () => {
    window.location.reload();
  };

  if (membershipStatus === 'loading' && !orgMember) {
    return (
      <PageFrame layout="centred">
        <LoadingSpinner size="lg" />
      </PageFrame>
    );
  }

  if (!user) {
    return (
      <PageFrame layout="centred">
        <div className="text-center">
          <PageTitle className="mb-4">Access Denied</PageTitle>
          <p className="mb-4 text-lureo-ink-subtle">Please log in to access your account.</p>
          <LureoButton variant="secondary" onClick={() => navigate('/')} className="h-9 rounded-lureo-radius-button-pill px-4">Go Home</LureoButton>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame layout="content" className="cursor-default overflow-x-hidden">
      <AccountPage
        onBack={() => navigate('/dashboard')}
        owner={isOwner}
        notice={membershipStatus === 'error' && (
          <Card elevation="high" className="mb-6">
            <EmptyState
              icon={<WarningCircleIcon />}
              title="Couldn't load your account details"
              action={
                <LureoButton variant="outline-3" className="h-9 px-4 rounded-lureo-radius-button-pill" onClick={membership.retry}>
                  Try again
                </LureoButton>
              }
            >
              Your name, organization and settings can't be changed until they load.
            </EmptyState>
          </Card>
        )}
        profile={
          <ProfileCard
            photo={<Avatar className="h-24 w-24" size="xl" refreshTrigger={avatarRefreshTrigger} />}
            onChangePhoto={() => setImageUploadOpen(true)}
            values={formData}
            onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            owner={isOwner}
            onKeyDown={handleProfileKeyDown}
            onNewEmailKeyDown={handleNewEmailKeyDown}
            saving={saving}
            canSave={canSaveProfile}
            onSave={updateProfile}
            namesDisabled={!membershipKnown}
            passwordPrompt={<PasswordLinkPrompt sent={resetSent} onSend={sendPasswordLink} />}
          />
        }
        security={
          <SecurityCard
            values={formData}
            onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            onCurrentPasswordKeyDown={focusOnEnter('new_password')}
            onNewPasswordKeyDown={focusOnEnter('confirm_password')}
            onConfirmPasswordKeyDown={handleConfirmPasswordKeyDown}
            saving={saving}
            onUpdatePassword={updatePassword}
            footer={<PasswordLinkPrompt sent={resetSent} onSend={sendPasswordLink} />}
          />
        }
        subscription={<SubscriptionCard orgInstance={orgInstance} />}
        settings={
          <AccountSettingsTab
            userId={user.id}
            orgMember={orgMember}
            orgInstance={orgInstance}
            membershipKnown={membershipKnown}
            updateMember={membership.updateMember}
            updateInstance={membership.updateInstance}
            onOpenCookiePreferences={openCookiePreferences}
            onSignOut={handleSignOut}
          />
        }
      />

      {/* Image Upload Dialog */}
      <UIImageUploadDialog
        open={imageUploadOpen}
        onOpenChange={setImageUploadOpen}
        onImageSelect={avatar.onImageSelect}
        onCropApplied={avatar.onCropApplied}
        title="Upload Profile Photo"
        description="Upload a profile photo for your account or generate one using AI."
        currentImage={avatar.currentImageUrl}
        enableCrop={true}
        closeOnRemove
        imageCategory="Avatar"
      />

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onVoucherClick={() => {
          setShowPlanModal(false);
          setShowVoucherModal(true);
        }}
      />

      {/* Voucher Redeem Modal */}
      <VoucherRedeemModal
        open={showVoucherModal}
        onClose={() => setShowVoucherModal(false)}
        onSuccess={handleVoucherSuccess}
      />
    </PageFrame>
  );
}
